import { InputNumber, Select } from 'antd';
import React, { useEffect, useId, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { selectLocation } from '../../../../util/store/location/constants';
import { locationActions } from '../../../../util/store/location/reducer';
import { getStore, useSelector } from '../../../../util/store/store';

export interface IStartPoint {
    id: string
    type: 'poi' | 'site' | 'factory'
    name: string
}

export interface ICostInformationValue {
    hourly_cost?: number | null
    cleaning_agent?: number | null
    start_point?: IStartPoint | Record<string, never>
}

export const validateCostInformation = (value: ICostInformationValue = {}) => {
    const filled = {
        hourly_cost: value.hourly_cost !== undefined && value.hourly_cost !== null,
        cleaning_agent: value.cleaning_agent !== undefined && value.cleaning_agent !== null,
        start_point: Boolean(value.start_point?.id),
    };
    const errors: Partial<Record<keyof ICostInformationValue, string>> = {};
    if (Object.values(filled).some(Boolean)) {
        if (!filled.hourly_cost) errors.hourly_cost = 'Please enter hourly cost.';
        if (!filled.cleaning_agent) errors.cleaning_agent = 'Please enter cleaning agent cost.';
        if (!filled.start_point) errors.start_point = 'Please select a start point.';
    }
    return errors;
};

interface IProps {
    value?: ICostInformationValue
    onChange?: (value: ICostInformationValue) => void
    disabled?: boolean
}

const CostInformation: React.FC<IProps> = ({
    value = {},
    onChange,
    disabled = false,
}) => {
    const helpId = useId();
    const dispatch = useDispatch();
    const sourceLocations = useSelector(selectLocation.allLocations);
    const loadingLocations = useSelector(selectLocation.loadingAllLocations);

    useEffect(() => {
        const { loadingAllLocations, allLocationsLoadedAt } = getStore().location;
        if (!loadingAllLocations && (!allLocationsLoadedAt || Date.now() - allLocationsLoadedAt >= 5 * 60 * 1000)) {
            dispatch(locationActions.getAllLocationsRequest());
        }
    }, [dispatch]);

    const locations = useMemo(() => {
        const result: IStartPoint[] = [];
        sourceLocations.forEach(location => {
            result.push({
                id: location.id,
                name: location.name,
                type: location.field as IStartPoint['type'],
            });
        });
        return result;
    }, [sourceLocations]);

    const errors = validateCostInformation(value);
    const getLocationKey = (location: IStartPoint) => JSON.stringify([location.type, location.id]);
    const startPoint = value.start_point?.id ? value.start_point as IStartPoint : undefined;
    const availableLocations = startPoint && !locations.some(location => getLocationKey(location) === getLocationKey(startPoint))
        ? [startPoint, ...locations]
        : locations;

    return <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2">
        <div>
            <label htmlFor={`${helpId}-hourly-cost`} className={`text-[15px] !mb-0 block !pl-[15px]`}>
                Hourly cost
            </label>
            <InputNumber<number>
                id={`${helpId}-hourly-cost`}
                className="!h-9 !w-full !pl-4 [&_.ant-input-number-input]:!h-[34px] [&_.ant-input-number-input]:!pl-0"
                min={0}
                controls={false}
                status={errors.hourly_cost ? 'error' : undefined}
                disabled={disabled}
                value={value.hourly_cost ?? null}
                onChange={hourly_cost => onChange?.({ ...value, hourly_cost })}
                suffix={<span className="text-sm italic">kr/h</span>}
            />
            {errors.hourly_cost && <div role="alert" className="mt-1 pl-[15px] text-sm text-[var(--colorDanger)]">{errors.hourly_cost}</div>}
        </div>
        <div>
            <label htmlFor={`${helpId}-cleaning-agent-cost`} className={`text-[15px] !mb-0 block !pl-[15px]`}>
                Cleaning agent cost
            </label>
            <InputNumber<number>
                id={`${helpId}-cleaning-agent-cost`}
                className="!h-9 !w-full !pl-4 [&_.ant-input-number-input]:!h-[34px] [&_.ant-input-number-input]:!pl-0"
                min={0}
                controls={false}
                status={errors.cleaning_agent ? 'error' : undefined}
                disabled={disabled}
                value={value.cleaning_agent ?? null}
                onChange={cleaning_agent => onChange?.({ ...value, cleaning_agent })}
                suffix={<span className="text-sm italic">kr</span>}
            />
            {errors.cleaning_agent && <div role="alert" className="mt-1 pl-[15px] text-sm text-[var(--colorDanger)]">{errors.cleaning_agent}</div>}
        </div>
        <div className="sm:col-span-2">
            <label htmlFor={`${helpId}-start-point`} className={`text-[15px] !mb-0 block !pl-[15px]`}>
                Default start location
            </label>
            <Select
                id={`${helpId}-start-point`}
                className="!h-9 w-full !pl-4 [&_.ant-select-placeholder]:italic"
                placeholder="Select a start point"
                loading={loadingLocations}
                showSearch
                optionFilterProp="label"
                status={errors.start_point ? 'error' : undefined}
                value={startPoint ? getLocationKey(startPoint) : undefined}
                options={availableLocations.map(location => ({
                    label: location.name,
                    value: getLocationKey(location),
                }))}
                onChange={key => {
                    const location = availableLocations.find(location => getLocationKey(location) === key);
                    if (location) onChange?.({ ...value, start_point: location });
                }}
                disabled={disabled}
                aria-describedby={helpId}
            />
            {errors.start_point && <div role="alert" className="mt-1 pl-[15px] text-sm text-[var(--colorDanger)]">{errors.start_point}</div>}
            <p id={helpId} className="mb-0 mt-1 text-sm italic text-[#687f80]">
                Ocean Planner uses this location as the starting point to calculate the traveling time to the operation location.
            </p>
        </div>
    </div>;
};

export default CostInformation;
